// A networking error's sentence with its role-aware action (spec 11 6.15.18), as deployment pages show it.
import { CircleX, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useApi } from '../../api/context';
import { useToast } from '../../components/toast';
import { Button } from '../../components/ui';
import { strings } from '../../strings';
import { networkErrorInfo, useManageNetworks, type NetworkErrorContext } from '../shared';

const s = strings.networks;

/** Creates a plan of the network and opens it (the "Plan network" action of network admins). */
export function PlanNetworkButton({ networkId, small = true }: { networkId: string; small?: boolean }) {
  const api = useApi();
  const toast = useToast();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  return (
    <Button
      className={small ? 'btn-sm' : undefined}
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          const run = await api.createNetworkRun(networkId, { kind: 'plan' });
          navigate(`/runs/${run.id}`);
        } catch (error) {
          toast.error(error);
          setBusy(false);
        }
      }}
    >
      {s.actions.planNetwork}
    </Button>
  );
}

/** The action next to a networking error (6.15.18): only the ones a deployment page can offer. */
export function NetworkErrorAction({
  code,
  networkId,
  network,
  deploymentId,
  deployment,
}: {
  code: string;
  networkId: string | null | undefined;
  network: string | null | undefined;
  deploymentId: string;
  deployment: string;
}) {
  const admin = useManageNetworks();
  const info = networkErrorInfo(code, { network, deployment });
  if (!info?.action || (info.adminOnly && !admin)) return null;
  switch (info.action) {
    case 'plan-network':
      return networkId ? <PlanNetworkButton networkId={networkId} /> : null;
    case 'open-network-settings':
      return networkId ? (
        <Link className="btn btn-sm" to={`/networks/${networkId}?tab=settings#providers`}>
          {s.actions.openSettings(network ?? s.nav.kind)}
        </Link>
      ) : null;
    case 'open-topology':
      return networkId ? (
        <Link className="btn btn-sm" to={`/networks/${networkId}?tab=settings#topology`}>
          {code === 'MESH_TOO_LARGE'
            ? strings.networks.topology.switchToHubSpoke
            : strings.networks.lists.settings.sections.topology}
        </Link>
      ) : null;
    case 'open-traffic':
      return networkId ? (
        <Link className="btn btn-sm" to={`/networks/${networkId}?tab=traffic&decision=pending`}>
          {code === 'NETWORK_REQUEST_PENDING'
            ? strings.networks.requests.approveEllipsis
            : strings.networks.requests.tab}
        </Link>
      ) : null;
    case 'add-supernet':
      return networkId ? (
        <Link className="btn btn-sm" to={`/networks/${networkId}?tab=address-plan`}>
          {s.actions.addSupernet}
        </Link>
      ) : null;
    case 'open-deployment-settings':
    case 'detach':
      return (
        <Link className="btn btn-sm" to={`/deployments/${deploymentId}?tab=settings&section=network`}>
          {info.action === 'detach' ? s.actions.detach : s.actions.openDeploymentSettings(deployment)}
        </Link>
      );
    default:
      return null;
  }
}

/** A networking Problem (a PATCH's 4xx) as a callout with its sentence and action; other codes show the server's text. */
export function NetworkProblemCallout({
  code,
  message,
  networkId,
  context,
  deploymentId,
}: {
  code: string;
  message: string;
  networkId: string | null | undefined;
  context: NetworkErrorContext & { deployment: string };
  deploymentId: string;
}) {
  const info = networkErrorInfo(code, { ...context, message });
  const warning = code === 'IPAM_SUBNET_TOO_SMALL';
  const Icon = warning ? TriangleAlert : CircleX;
  return (
    <div
      role="alert"
      className={`callout ${warning ? 'callout-warning' : 'callout-danger'} mb-3`}
      data-testid="network-problem"
    >
      <Icon aria-hidden className="size-4 shrink-0" />
      <div className="min-w-0 flex-1">
        <p>{info?.sentence ?? message}</p>
        <p className="mt-0.5 font-mono text-xs">{code}</p>
      </div>
      <div className="flex shrink-0 flex-wrap gap-2">
        <NetworkErrorAction
          code={code}
          networkId={networkId}
          network={context.network}
          deploymentId={deploymentId}
          deployment={context.deployment}
        />
      </div>
    </div>
  );
}
